import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, LogOut, Inbox, Send, Megaphone, Pin, Search, Plus, X, Trash2, ArrowLeft, Reply, ShieldCheck, Bell, UsersRound, Lock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { staffApi } from '@/lib/staffApi';
import { toast } from 'sonner';
import NotificationBell from '../../components/NotificationBell';
import ChangePasswordModal from '../../components/ChangePasswordModal';

const STAFF_ROLES = new Set(['owner', 'admin', 'tutor', 'support', 'finance', 'staff']);
const PRIV = new Set(['owner', 'admin']);

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch (_e) { return iso || '—'; } };

const Pill = ({ active, onClick, children, badge, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`h-9 px-4 rounded-full text-xs font-bold uppercase tracking-wider transition flex items-center gap-1.5 ${active ? 'bg-black text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-100'}`}>
    {children}
    {badge > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-red-700 text-white text-[10px] font-bold">{badge}</span>}
  </button>
);

const RoleBadge = ({ role }) => {
  const styles = {
    owner:   'bg-amber-500 text-white',
    admin:   'bg-red-700 text-white',
    tutor:   'bg-black text-[#D3D86F]',
    support: 'bg-teal-700 text-white',
    finance: 'bg-blue-700 text-white',
    staff:   'bg-gray-700 text-white',
  }[role] || 'bg-gray-300 text-gray-800';
  return <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${styles}`}>{role}</span>;
};

const ComposeModal = ({ open, onClose, onSent, colleagues, me, defaultAnnouncement = false }) => {
  const [form, setForm] = useState({ subject: '', body: '', recipient_ids: [], is_announcement: defaultAnnouncement, audience_role: 'all', related_booking_id: '' });
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('');
  const canAnnounce = PRIV.has((me?.role || '').toLowerCase());

  useEffect(() => { if (open) setForm({ subject: '', body: '', recipient_ids: [], is_announcement: defaultAnnouncement && canAnnounce, audience_role: 'all', related_booking_id: '' }); }, [open, defaultAnnouncement, canAnnounce]);

  if (!open) return null;

  const filtered = (colleagues || []).filter((c) => !filter || (c.name || '').toLowerCase().includes(filter.toLowerCase()) || c.email.toLowerCase().includes(filter.toLowerCase()) || (c.role || '').includes(filter.toLowerCase()));

  const toggle = (id) => setForm((f) => ({ ...f, recipient_ids: f.recipient_ids.includes(id) ? f.recipient_ids.filter((x) => x !== id) : [...f.recipient_ids, id] }));

  const submit = async () => {
    setBusy(true);
    try {
      await staffApi.send({
        subject: form.subject.trim(),
        body: form.body.trim(),
        recipient_ids: form.is_announcement ? [] : form.recipient_ids,
        is_announcement: form.is_announcement,
        audience_role: form.is_announcement ? form.audience_role : null,
        related_booking_id: form.related_booking_id.trim() || null,
      });
      toast.success(form.is_announcement ? 'Announcement posted' : 'Message sent');
      onSent();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => !busy && onClose()} data-testid="staff-compose-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <div className="text-sm font-bold uppercase tracking-wider flex items-center gap-2"><Send className="h-4 w-4 text-red-700" /> {form.is_announcement ? 'New announcement' : 'New message'}</div>
          <button onClick={() => !busy && onClose()} className="h-7 w-7 rounded-lg hover:bg-gray-100 flex items-center justify-center" data-testid="staff-compose-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="px-5 py-4 space-y-3 overflow-y-auto flex-1">
          {canAnnounce && (
            <label className="flex items-center gap-2 text-xs cursor-pointer p-2 rounded-lg bg-amber-50 border border-amber-200">
              <input type="checkbox" checked={form.is_announcement} onChange={(e) => setForm({ ...form, is_announcement: e.target.checked })} className="h-4 w-4 accent-red-700" data-testid="staff-compose-announcement" />
              <Megaphone className="h-3.5 w-3.5 text-amber-700" />
              Post as Winhouse-wide announcement
              {form.is_announcement && (
                <select value={form.audience_role} onChange={(e) => setForm({ ...form, audience_role: e.target.value })} className="ml-auto h-7 px-2 rounded border border-amber-200 text-[11px] bg-white" data-testid="staff-compose-audience">
                  <option value="all">All staff</option>
                  <option value="admin">Admins only</option>
                  <option value="tutor">Tutors only</option>
                  <option value="support">Support only</option>
                  <option value="finance">Finance only</option>
                </select>
              )}
            </label>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Subject</div>
            <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Brief subject…"
              className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200" data-testid="staff-compose-subject" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Message</div>
            <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={7} placeholder="Type your message…"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 leading-relaxed" data-testid="staff-compose-body" />
          </div>
          {!form.is_announcement && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Recipients · {form.recipient_ids.length} selected</div>
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 h-8 mb-2">
                <Search className="h-3 w-3 text-gray-400" />
                <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter colleagues…" className="flex-1 bg-transparent text-xs focus:outline-none" data-testid="staff-compose-filter" />
              </div>
              <div className="max-h-56 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100" data-testid="staff-compose-recipients">
                {filtered.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 cursor-pointer" data-testid={`staff-compose-recipient-${c.id}`}>
                    <input type="checkbox" checked={form.recipient_ids.includes(c.id)} onChange={() => toggle(c.id)} className="h-4 w-4 accent-red-700" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold text-gray-900 truncate flex items-center gap-1.5">{c.name || c.email} <RoleBadge role={c.role} /></div>
                      <div className="text-[10px] text-gray-500 truncate">{c.email}</div>
                    </div>
                  </label>
                ))}
                {filtered.length === 0 && <div className="text-[11px] text-gray-400 italic text-center py-4">No colleagues match</div>}
              </div>
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Linked booking (optional)</div>
            <input value={form.related_booking_id} onChange={(e) => setForm({ ...form, related_booking_id: e.target.value })} placeholder="booking_id (helps tag discussions)"
              className="w-full h-9 px-3 rounded-lg border border-gray-200 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-red-200" data-testid="staff-compose-booking" />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2 bg-gray-50 rounded-b-2xl flex-shrink-0">
          <button onClick={() => !busy && onClose()} disabled={busy} className="h-9 px-4 rounded-full bg-white border border-gray-200 text-xs font-bold uppercase tracking-wider" data-testid="staff-compose-cancel">Cancel</button>
          <button onClick={submit} disabled={busy || !form.subject.trim() || !form.body.trim() || (!form.is_announcement && form.recipient_ids.length === 0)}
            className="h-9 px-5 rounded-full bg-red-700 hover:bg-red-800 text-white text-xs font-bold uppercase tracking-wider disabled:opacity-40 flex items-center gap-1.5" data-testid="staff-compose-submit">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} {form.is_announcement ? 'Post' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ThreadView = ({ thread, onBack, onChanged, me }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const canPin = PRIV.has((me?.role || '').toLowerCase());

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await staffApi.thread(thread.thread_id); setMessages(r.items || []); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not load thread'); }
    finally { setLoading(false); }
  }, [thread.thread_id]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try { await staffApi.reply(thread.thread_id, reply.trim()); setReply(''); await load(); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Reply failed'); }
    finally { setBusy(false); }
  };

  const togglePin = async (m) => {
    try { await staffApi.pin(m.message_id, !m.pinned); load(); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Pin failed'); }
  };

  const remove = async (m) => {
    if (!window.confirm('Remove this message?')) return;
    try { await staffApi.remove(m.message_id); load(); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-red-700" /></div>;

  return (
    <div data-testid="staff-thread-view">
      <button onClick={onBack} className="text-[11px] text-gray-500 uppercase tracking-wider font-bold flex items-center gap-1 mb-3 hover:text-gray-900" data-testid="staff-thread-back">
        <ArrowLeft className="h-3 w-3" /> Back to list
      </button>
      <div className="text-lg font-bold text-gray-900 mb-1">{messages[0]?.subject}</div>
      {messages[0]?.related_booking_id && (
        <div className="text-[11px] text-gray-500 mb-3">Linked booking: <span className="font-mono">{messages[0].related_booking_id}</span></div>
      )}
      <div className="space-y-3 mb-4">
        {messages.map((m) => (
          <div key={m.message_id} className={`p-4 rounded-2xl border ${m.sender_id === me?.id ? 'bg-red-50/60 border-red-100' : 'bg-white border-gray-200'} ${m.pinned ? 'ring-2 ring-amber-300' : ''}`} data-testid={`staff-message-${m.message_id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
              <div className="flex items-center gap-2 text-xs font-bold">
                <span className="text-gray-900">{m.sender_name || m.sender_email}</span>
                <RoleBadge role={m.sender_role} />
                {m.is_announcement && <span className="px-1.5 py-0.5 rounded bg-amber-500 text-white text-[9px] font-bold uppercase tracking-wider flex items-center gap-0.5"><Megaphone className="h-2.5 w-2.5" /> Announcement</span>}
                {m.pinned && <span className="px-1.5 py-0.5 rounded bg-amber-700 text-white text-[9px] font-bold uppercase tracking-wider flex items-center gap-0.5"><Pin className="h-2.5 w-2.5" /> Pinned</span>}
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-gray-500">{fmt(m.created_at)}</span>
                {canPin && m.is_announcement && (
                  <button onClick={() => togglePin(m)} className="h-7 w-7 rounded hover:bg-amber-50 text-amber-700 flex items-center justify-center" data-testid={`staff-pin-${m.message_id}`}><Pin className="h-3 w-3" /></button>
                )}
                {(m.sender_id === me?.id || canPin) && (
                  <button onClick={() => remove(m)} className="h-7 w-7 rounded hover:bg-red-50 text-red-700 flex items-center justify-center" data-testid={`staff-delete-${m.message_id}`}><Trash2 className="h-3 w-3" /></button>
                )}
              </div>
            </div>
            <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{m.body}</div>
          </div>
        ))}
      </div>
      <div className="bg-white border border-gray-200 rounded-2xl p-4" data-testid="staff-reply-box">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2 flex items-center gap-1"><Reply className="h-3 w-3" /> Reply</div>
        <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3} placeholder="Type your reply…"
          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200" data-testid="staff-reply-input" />
        <div className="flex justify-end mt-2">
          <button onClick={send} disabled={busy || !reply.trim()} className="h-9 px-4 rounded-full bg-red-700 hover:bg-red-800 text-white text-xs font-bold uppercase tracking-wider disabled:opacity-40 flex items-center gap-1.5" data-testid="staff-reply-submit">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Reply
          </button>
        </div>
      </div>
    </div>
  );
};

const StaffHubPage = () => {
  const navigate = useNavigate();
  const { user, logout, loading } = useAuth();
  const [tab, setTab] = useState('inbox');
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [colleagues, setColleagues] = useState([]);
  const [me, setMe] = useState(null);
  const [unread, setUnread] = useState({ unread: 0, unread_direct: 0, unread_announcements: 0 });
  const [loadingData, setLoadingData] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeAnnouncement, setComposeAnnouncement] = useState(false);
  const [search, setSearch] = useState('');
  const [pwOpen, setPwOpen] = useState(false);

  const refreshList = useCallback(async (box) => {
    setLoadingData(true);
    try { const r = await staffApi.list({ box: box || tab, q: search || undefined }); setThreads(r.items || []); }
    catch (_e) {} finally { setLoadingData(false); }
  }, [tab, search]);

  const refreshUnread = useCallback(async () => {
    try { setUnread(await staffApi.unreadCount()); } catch (_e) {}
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/staff/login', { replace: true }); return; }
    const role = (user.role || '').toLowerCase();
    if (!STAFF_ROLES.has(role)) {
      // students / parents land on their own portal
      if (role === 'student' || role === 'parent') navigate('/student/dashboard', { replace: true });
      else navigate('/', { replace: true });
      return;
    }
    staffApi.colleagues().then((r) => { setColleagues(r.items || []); setMe(r.me); }).catch(() => {});
    refreshList(tab);
    refreshUnread();
  }, [loading, user, navigate, tab, refreshList, refreshUnread]);

  useEffect(() => { refreshList(tab); }, [tab, refreshList]);

  const counts = useMemo(() => ({
    inbox: unread.unread_direct || 0,
    announcements: unread.unread_announcements || 0,
  }), [unread]);

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]"><Loader2 className="h-6 w-6 animate-spin text-red-700" /></div>;

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="staff-hub-page">
      <header className="bg-black text-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-5 w-5 text-[#D3D86F]" />
            <div>
              <div className="text-[10px] uppercase tracking-[3px] text-[#D3D86F] font-bold">WINHOUSE STAFF HUB</div>
              <div className="text-sm font-bold">{me?.name || user.name || user.email} · <span className="text-[#D3D86F]">{me?.role || user.role}</span></div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell accent="gold" testidPrefix="staff-" />
            {unread.unread > 0 && (
              <div className="h-9 px-3 rounded-full bg-red-700 text-white text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5" data-testid="staff-unread-badge">
                <Bell className="h-3 w-3" /> {unread.unread} unread
              </div>
            )}
            <button onClick={() => navigate('/admin/users')} className="hidden sm:flex h-9 px-3 rounded-full bg-white/10 hover:bg-white/20 text-[11px] font-bold uppercase tracking-wider items-center gap-1.5" data-testid="staff-nav-users"><UsersRound className="h-3 w-3" /> Staff</button>
            <button onClick={() => setPwOpen(true)} className="h-9 px-3 rounded-full bg-white/10 hover:bg-white/20 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5" data-testid="nav-change-password"><Lock className="h-3 w-3" /> Password</button>
            <button onClick={() => { logout(); navigate('/staff/login'); }} className="h-9 px-3 rounded-full bg-white/10 hover:bg-white/20 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5" data-testid="portal-logout"><LogOut className="h-3 w-3" /> Sign out</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6 space-y-5">
        <div className="flex items-center gap-2 flex-wrap" data-testid="staff-tabs">
          <Pill active={tab === 'inbox'} onClick={() => { setSelected(null); setTab('inbox'); }} testid="staff-tab-inbox" badge={counts.inbox}><Inbox className="h-3.5 w-3.5" /> Inbox</Pill>
          <Pill active={tab === 'announcements'} onClick={() => { setSelected(null); setTab('announcements'); }} testid="staff-tab-announcements" badge={counts.announcements}><Megaphone className="h-3.5 w-3.5" /> Announcements</Pill>
          <Pill active={tab === 'sent'} onClick={() => { setSelected(null); setTab('sent'); }} testid="staff-tab-sent"><Send className="h-3.5 w-3.5" /> Sent</Pill>
          <div className="ml-auto flex items-center gap-2">
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 h-9 w-44 sm:w-64">
              <Search className="h-3.5 w-3.5 text-gray-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') refreshList(tab); }} placeholder="Search messages…" className="flex-1 bg-transparent text-xs focus:outline-none" data-testid="staff-search" />
            </div>
            {PRIV.has((me?.role || '').toLowerCase()) && (
              <button onClick={() => { setComposeAnnouncement(true); setComposeOpen(true); }} className="h-9 px-4 rounded-full bg-black text-[#D3D86F] hover:bg-gray-900 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5" data-testid="staff-compose-announcement-btn">
                <Megaphone className="h-3 w-3" /> Announce
              </button>
            )}
            <button onClick={() => { setComposeAnnouncement(false); setComposeOpen(true); }} className="h-9 px-4 rounded-full bg-red-700 hover:bg-red-800 text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1.5" data-testid="staff-compose-btn">
              <Plus className="h-3 w-3" /> Compose
            </button>
          </div>
        </div>

        {selected ? (
          <section className="bg-[#FAF9F6]">
            <ThreadView thread={selected} onBack={() => { setSelected(null); refreshList(tab); refreshUnread(); }} onChanged={refreshUnread} me={me} />
          </section>
        ) : (
          <section className="bg-white border border-gray-200 rounded-2xl overflow-hidden" data-testid="staff-thread-list">
            {loadingData ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-red-700" /></div>
            ) : threads.length === 0 ? (
              <div className="text-center text-xs text-gray-400 italic py-12" data-testid="staff-list-empty">
                {tab === 'inbox' && 'Your inbox is empty.'}
                {tab === 'sent' && "You haven't sent any messages yet."}
                {tab === 'announcements' && 'No announcements posted yet.'}
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {threads.map((t) => {
                  const m = t.latest;
                  const unreadHere = t.unread_in_thread > 0;
                  return (
                    <li key={t.thread_id}>
                      <button onClick={() => setSelected(t)} className={`w-full flex items-start gap-3 p-4 text-left transition ${unreadHere ? 'bg-red-50/40 hover:bg-red-50' : 'hover:bg-gray-50'}`} data-testid={`staff-thread-${t.thread_id}`}>
                        <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${m.is_announcement ? 'bg-amber-500 text-white' : 'bg-black text-[#D3D86F]'}`}>
                          {m.is_announcement ? <Megaphone className="h-4 w-4" /> : <Inbox className="h-4 w-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {t.pinned_any && <Pin className="h-3 w-3 text-amber-700" />}
                            <div className={`text-sm truncate ${unreadHere ? 'font-bold text-gray-900' : 'font-semibold text-gray-700'}`}>{m.subject}</div>
                            {t.messages_count > 1 && <span className="text-[10px] text-gray-500 bg-gray-100 rounded-full px-1.5">{t.messages_count}</span>}
                            {unreadHere && <span className="h-2 w-2 rounded-full bg-red-700" />}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-500 flex-wrap">
                            <span className="font-bold text-gray-700">{m.sender_name || m.sender_email}</span>
                            <RoleBadge role={m.sender_role} />
                            <span>·</span>
                            <span>{fmt(m.created_at)}</span>
                            {m.related_booking_id && <span className="ml-1 px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 text-[9px] font-mono">booking</span>}
                          </div>
                          <div className="text-[11px] text-gray-600 mt-1 truncate">{m.body}</div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}
      </main>

      <ComposeModal open={composeOpen} onClose={() => setComposeOpen(false)} colleagues={colleagues} me={me} defaultAnnouncement={composeAnnouncement} onSent={() => { refreshList(tab); refreshUnread(); }} />
      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
    </div>
  );
};

export default StaffHubPage;
